import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  Mail, 
  Send, 
  Clock, 
  Settings, 
  Loader2, 
  Plus, 
  Trash2, 
  Eye,
  CheckCircle,
  AlertCircle,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ReportsConfigSection = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [logs, setLogs] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const fetchConfig = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching report config:', error);
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSchedulerStatus = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/scheduler-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  }, [token]);

  const fetchLogs = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/logs?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
    fetchLogs();
    fetchSchedulerStatus();
  }, [fetchConfig, fetchLogs, fetchSchedulerStatus]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/reports/config`, {
        recipients: config.recipients,
        include_low_stock_alerts: config.include_low_stock_alerts,
        low_stock_threshold: config.low_stock_threshold
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/reports/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`Rapport envoyé à ${response.data.recipients?.length || 0} destinataires`);
        fetchLogs();
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API_URL}/reports/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleRecipientActive = (email) => {
    setConfig(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.email === email ? { ...r, active: !r.active } : r
      )
    }));
  };

  const removeRecipient = (email) => {
    setConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.email !== email)
    }));
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    if (config.recipients.some(r => r.email === newEmail)) {
      toast.error('Cet email existe déjà');
      return;
    }
    setConfig(prev => ({
      ...prev,
      recipients: [...prev.recipients, { email: newEmail, name: newName || null, active: true }]
    }));
    setNewEmail('');
    setNewName('');
    setAddEmailOpen(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
  };

  if (loading) {
    return (
      <Card className="border-[#E8DDD4]">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#C9A086]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E8DDD4]">
      <CardHeader className="pb-4">
        <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#C9A086]" />
          Rapports Automatiques Hebdomadaires
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Schedule Info */}
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900">Envoi automatique activé</p>
            {schedulerStatus?.next_run_formatted ? (
              <p className="text-xs text-green-700">
                Prochain envoi : <strong>{schedulerStatus.next_run_formatted}</strong>
              </p>
            ) : (
              <p className="text-xs text-green-700">Chaque lundi à 8h00 (heure d'Abidjan)</p>
            )}
          </div>
          <Badge className="bg-green-100 text-green-700 text-xs">Actif</Badge>
        </div>

        {/* Recipients */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium text-[#5D4E37]">Destinataires</Label>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setAddEmailOpen(true)}
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter
            </Button>
          </div>
          <div className="space-y-2">
            {config?.recipients?.map((recipient, idx) => (
              <div 
                key={idx} 
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  recipient.active ? 'bg-white border-[#E8DDD4]' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={recipient.active} 
                    onCheckedChange={() => toggleRecipientActive(recipient.email)}
                  />
                  <div>
                    <p className={`text-sm font-medium ${recipient.active ? 'text-[#0F172A]' : 'text-gray-400'}`}>
                      {recipient.name || recipient.email}
                    </p>
                    {recipient.name && (
                      <p className={`text-xs ${recipient.active ? 'text-[#78716C]' : 'text-gray-400'}`}>
                        {recipient.email}
                      </p>
                    )}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => removeRecipient(recipient.email)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#5D4E37]">Options</Label>
          <div className="flex items-center justify-between p-3 bg-[#F5F0EB] rounded-lg">
            <div>
              <p className="text-sm font-medium text-[#0F172A]">Alertes stock bas</p>
              <p className="text-xs text-[#78716C]">
                Inclure les produits avec stock ≤ {config?.low_stock_threshold || 5} unités
              </p>
            </div>
            <Switch 
              checked={config?.include_low_stock_alerts || false}
              onCheckedChange={(checked) => setConfig(prev => ({
                ...prev,
                include_low_stock_alerts: checked
              }))}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-[#E8DDD4]">
          <Button 
            onClick={handleSaveConfig}
            disabled={saving}
            className="bg-[#C9A086] hover:bg-[#B8907A]"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
          <Button 
            variant="outline" 
            onClick={handlePreview}
            className="border-[#C9A086] text-[#C9A086]"
          >
            <Eye className="w-4 h-4 mr-2" />
            Prévisualiser
          </Button>
          <Button 
            variant="outline"
            onClick={handleSendNow}
            disabled={sending}
            className="border-green-500 text-green-600 hover:bg-green-50"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer maintenant
          </Button>
        </div>

        {/* Logs */}
        {logs.length > 0 && (
          <div className="pt-4 border-t border-[#E8DDD4]">
            <Label className="text-sm font-medium text-[#5D4E37] mb-3 block">Derniers envois</Label>
            <div className="space-y-2">
              {logs.map((log, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm p-2 bg-[#F5F0EB]/50 rounded">
                  <div className="flex items-center gap-2">
                    {log.success ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="text-[#5D4E37]">
                      {log.period?.label || 'Rapport'}
                    </span>
                  </div>
                  <span className="text-xs text-[#78716C]">
                    {new Date(log.sent_at).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Email Dialog */}
        <Dialog open={addEmailOpen} onOpenChange={setAddEmailOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un destinataire</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  placeholder="email@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div>
                <Label>Nom (optionnel)</Label>
                <Input 
                  placeholder="Prénom Nom"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddEmailOpen(false)}>Annuler</Button>
              <Button onClick={addRecipient} className="bg-[#C9A086]">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
            <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-zinc-200">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#C9A086]" />
                Prévisualisation du rapport
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
            {previewLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-[#C9A086]" />
              </div>
            ) : previewData ? (
              <div className="space-y-4">
                <p className="text-sm text-[#78716C]">{previewData.period?.label}</p>
                
                {/* Summary */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-700">CA</p>
                    <p className="text-lg font-bold text-green-800">
                      {formatCurrency(previewData.summary?.total_revenue || 0)}
                    </p>
                    <p className="text-xs text-green-600">
                      {previewData.summary?.revenue_variation >= 0 ? '+' : ''}
                      {previewData.summary?.revenue_variation}% vs sem. préc.
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700">Ventes</p>
                    <p className="text-lg font-bold text-blue-800">
                      {previewData.summary?.total_sales || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-xs text-orange-700">Panier moyen</p>
                    <p className="text-lg font-bold text-orange-800">
                      {formatCurrency(previewData.summary?.average_basket || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-700">Diagnostics</p>
                    <p className="text-lg font-bold text-purple-800">
                      {previewData.summary?.total_diagnoses || 0}
                    </p>
                  </div>
                </div>

                {/* Top Products */}
                <div>
                  <p className="text-sm font-medium text-[#5D4E37] mb-2">Top 5 Produits</p>
                  {previewData.top_products?.length > 0 ? (
                    <div className="space-y-1">
                      {previewData.top_products.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-[#F5F0EB]/50 rounded">
                          <span>{i + 1}. {p.name}</span>
                          <span className="text-[#78716C]">{p.quantity} unités</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#78716C]">Aucune vente</p>
                  )}
                </div>

                {/* Agents */}
                <div>
                  <p className="text-sm font-medium text-[#5D4E37] mb-2">Classement Conseillers</p>
                  {previewData.agents_ranking?.length > 0 ? (
                    <div className="space-y-1">
                      {previewData.agents_ranking.map((a, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-[#F5F0EB]/50 rounded">
                          <span>{a.rank}. {a.name}</span>
                          <span className="text-[#78716C]">{a.sales} ventes • {formatCurrency(a.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#78716C]">Aucune donnée</p>
                  )}
                </div>

                {/* Low Stock */}
                {previewData.low_stock_alerts?.length > 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium text-yellow-800 mb-2">Alertes Stock Bas</p>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {previewData.low_stock_alerts.map((p, i) => (
                        <li key={i}>• {p.name} - {p.stock_quantity} unités</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ReportsConfigSection;
